import { View, Text, TextInput, TouchableOpacity, Switch, StyleSheet } from 'react-native'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useTranslation } from 'react-i18next'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'

// ─── Re-export componenti condivisi ───────────────────────────────────
export { Card, SectionLabel, SubHeader } from '../ui'

// ─── Msg ──────────────────────────────────────────────────────────────
export function Msg({ type, text }) {
  const tc = useThemeColors()
  if (!text) return null
  const c = {
    success: { bg: tc.successBg, border: tc.successBorder, color: tc.success },
    error:   { bg: tc.rosaSubtle, border: tc.rosaBorder, color: tc.rosaText },
  }[type] ?? { bg: tc.infoBg, border: tc.infoBorder, color: tc.info }
  return (
    <View
      style={[styles.msg, { backgroundColor: c.bg, borderColor: c.border }]}
      accessibilityRole={type === 'error' ? 'alert' : undefined}
      accessibilityLiveRegion="polite"
    >
      <Text style={[styles.msgText, { color: c.color }]}>{text}</Text>
    </View>
  )
}

// ─── StyledInput ──────────────────────────────────────────────────────
export function StyledInput({ value, onChangeText, placeholder, secureTextEntry, onSubmitEditing, style }) {
  const tc = useThemeColors()
  return (
    <TextInput
      style={[styles.input, { backgroundColor: tc.bgCard2, borderColor: tc.border, color: tc.text }, style]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={tc.textDim}
      secureTextEntry={secureTextEntry}
      autoCapitalize="none"
      autoCorrect={false}
      onSubmitEditing={onSubmitEditing}
    />
  )
}

// ─── Label ────────────────────────────────────────────────────────────
export function Label({ children }) {
  const tc = useThemeColors()
  return <Text style={[styles.label, { color: tc.textMuted }]}>{children}</Text>
}

// ─── Toggle ───────────────────────────────────────────────────────────
export function Toggle({ active, onToggle, disabled }) {
  const tc = useThemeColors()
  return (
    <Switch
      value={active}
      onValueChange={onToggle}
      disabled={disabled}
      trackColor={{ false: colors.switchTrack, true: tc.rosa }}
      thumbColor={active ? tc.brandText : colors.switchThumb}
      ios_backgroundColor="#444"
    />
  )
}

// ─── PrivacyPicker ────────────────────────────────────────────────────
export function PrivacyPicker({ isPublic, onSelect, labelPublic, labelPrivate, iconPublic = 'globe-outline', iconPrivate = 'lock-closed-outline' }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  return (
    <View style={styles.privacyRow} accessibilityRole="radiogroup">
      {[{ v: true, l: labelPublic ?? t('league.public') }, { v: false, l: labelPrivate ?? t('league.private') }].map(o => (
        <TouchableOpacity
          key={String(o.v)}
          onPress={() => onSelect(o.v)}
          accessibilityRole="radio"
          accessibilityState={{ checked: isPublic === o.v }}
          accessibilityLabel={o.l}
          style={[
            styles.privacyBtn,
            { backgroundColor: isPublic === o.v ? tc.rosa : tc.bgCard2, borderColor: isPublic === o.v ? tc.rosa : tc.border },
          ]}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Ionicons name={o.v ? iconPublic : iconPrivate} size={13} color={isPublic === o.v ? tc.brandText : tc.textMuted} />
            <Text style={[styles.privacyBtnText, { color: isPublic === o.v ? tc.brandText : tc.textMuted }]}>{o.l}</Text>
          </View>
        </TouchableOpacity>
      ))}
    </View>
  )
}

// ─── RowBtn ───────────────────────────────────────────────────────────
export function RowBtn({ onPress, disabled, variant = 'neutral', accessibilityLabel, children }) {
  const tc = useThemeColors()
  const bg     = variant === 'rosa'    ? tc.rosaSubtle :
                 variant === 'warning' ? tc.warningBg :
                 variant === 'danger'  ? tc.rosaSubtle : tc.bgCard2
  const border = variant === 'rosa'    ? tc.rosaBorder :
                 variant === 'warning' ? tc.warningBorder :
                 variant === 'danger'  ? tc.rosaBorder : tc.border
  const col    = variant === 'rosa'    ? tc.rosa :
                 variant === 'warning' ? tc.warning :
                 variant === 'danger'  ? tc.rosa : tc.textMuted
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? (typeof children === 'string' ? children : undefined)}
      accessibilityState={{ disabled: !!disabled }}
      style={[styles.rowBtn, { backgroundColor: bg, borderColor: border, opacity: disabled ? 0.5 : 1 }]}
    >
      <Text style={[styles.rowBtnText, { color: col }]}>{children}</Text>
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  msg:           { borderWidth: 1, borderRadius: 8, padding: 10, paddingHorizontal: 12, marginBottom: 12 },
  msgText:       { fontSize: 12, lineHeight: 18 },
  input:         { borderWidth: 1, borderRadius: 10, padding: 12, paddingHorizontal: 14, fontSize: 14, marginBottom: 12 },
  label:         { fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  privacyRow:    { flexDirection: 'row', gap: 10, marginBottom: 12 },
  privacyBtn:    { flex: 1, minHeight: 44, justifyContent: 'center', borderRadius: 10, borderWidth: 1, padding: 11, alignItems: 'center' },
  privacyBtnText:{ fontFamily: fonts.condensedBold, fontSize: 13 },
  rowBtn:        { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  rowBtnText:    { fontSize: 12, fontFamily: fonts.condensedBold },
})